import { useState } from 'react'
import type { EventPageEvent } from '../data/types'
import { formatDate } from '../data/format'
import { FlyerModal } from './FlyerModal'
import { useIsMobile } from '../hooks/useIsMobile'
import styles from './Hero.module.css'

export function Hero({ event }: { event: EventPageEvent }) {
  const [flyerOpen, setFlyerOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = [event.city, event.state].filter(Boolean).join(', ') || event.altVenue
  const dateRange = `${formatDate(event.openPracticeStart)} – ${formatDate(event.mainsStart)}`

  return (
    <section className={styles.hero}>
      {/* Mobile: the flyer is the page's hero - full width, on a blurred backdrop of itself, tap
          to enlarge (Main.dc.html). Desktop keeps the smaller side-by-side thumbnail treatment. */}
      {isMobile && event.flyerUrl ? (
        <button type="button" className={styles.mobileFlyerBand} onClick={() => setFlyerOpen(true)} aria-label="View the full event flyer">
          <img src={event.flyerUrl} alt="" aria-hidden="true" className={styles.mobileFlyerBackdrop} />
          <div className={styles.mobileFlyerFrame}>
            <img src={event.flyerUrl} alt="Event flyer" className={styles.mobileFlyerImg} />
          </div>
          {/* Caption below the image, not overlaid on it - a real flyer's own content can extend
              right to its edges (confirmed live: the badge sitting on the image hid a real
              event's date/website line), so nothing sits on top of the poster itself. */}
          <span className={styles.flyerCaption}>Tap to enlarge</span>
        </button>
      ) : null}

      <div className={styles.inner}>
        {!isMobile && event.flyerUrl ? (
          <button type="button" className={styles.flyerButton} onClick={() => setFlyerOpen(true)} aria-label="View the full event flyer">
            <img src={event.flyerUrl} alt="Event flyer" className={styles.flyerImg} />
            <span className={styles.flyerCaption}>View full size</span>
          </button>
        ) : null}

        <div className={styles.details}>
          <div className={styles.dateLine}>{dateRange}</div>
          {/* Track name (no "Hosted by" prefix) + city, sits directly under the date line, above
              the title - context/presenter first, headline second. */}
          {event.trackName ? (
            <div className={styles.trackLine}>
              <strong>{event.trackName}</strong>
              {location ? ` · ${location}` : ''}
            </div>
          ) : null}
          <h1 className={styles.title}>{event.name}</h1>

          {/* Desktop only - on mobile the flyer band above is itself the tap target, and there's
              no room for a second set of actions crowding the title. */}
          {!isMobile && event.flyerUrl ? (
            <div className={styles.actions}>
              <button type="button" className={styles.actionBtn} onClick={() => setFlyerOpen(true)}>
                View flyer
              </button>
              <a href={event.flyerUrl} download className={styles.actionBtn}>
                Download flyer
              </a>
            </div>
          ) : null}
        </div>
      </div>

      {flyerOpen ? <FlyerModal src={event.flyerUrl} onClose={() => setFlyerOpen(false)} /> : null}
    </section>
  )
}
